import { type FilterViewModel } from '@/infra/FiltersViewModel'
import { isNil } from 'lodash-es'
import { action, computed, observable } from 'mobx'

/**
 * A filter for a boolean flag.
 */
export class FlagFilter<TKey> implements FilterViewModel<TKey, boolean> {
  /**
   * @inheritDoc
   */
  key: TKey

  /**
   * @inheritDoc
   */
  title: string

  /**
   * @inheritDoc
   */
  @observable
  accessor value: boolean

  /**
   * @inheritDoc
   */
  readonly editing = false

  constructor(key: TKey, title: string, value = false) {
    this.key = key
    this.title = title
    this.value = value
  }

  /**
   * @inheritDoc
   */
  @computed
  get valid() {
    return !isNil(this.value)
  }

  /**
   * @inheritDoc
   */
  @computed
  get valueText() {
    return this.value ? 'Yes' : 'No'
  }

  /**
   * @inheritDoc
   */
  toString() {
    return this.title
  }

  /**
   * @inheritDoc
   */
  @action.bound
  clearValue(): void {
    this.value = false
  }

  /**
   * Sets the flag filter's value.
   */
  @action.bound
  setValue(value: boolean): void {
    this.value = value
  }

  /**
   * @inheritDoc
   */
  // eslint-disable-next-line @typescript-eslint/no-empty-function -- a flag toggles immediately, so there is no editing session
  startEditing(): void {}

  /**
   * @inheritDoc
   */
  // eslint-disable-next-line @typescript-eslint/no-empty-function -- a flag toggles immediately, so there is no editing session
  stopEditing(): void {}

  /**
   * @inheritDoc
   */
  serialize() {
    return JSON.stringify(this.value)
  }

  /**
   * @inheritDoc
   */
  @action.bound
  deserialize(serialized: unknown) {
    if (typeof serialized === 'string') {
      try {
        this.setValue(JSON.parse(serialized) as boolean)
      } catch {
        this.clearValue()
      }
    }
  }
}
